import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { showError } from '@utils';
import { useQuery } from 'react-query';
import { orderBy } from 'lodash-es';
import { QUERY_KEYS } from 'constants/queryKeys';

import { boardsServices, fleetVehiclesServices } from '@services';
import { ARNM_MAINTENANCE_TASK_STATUSES_NAMES } from 'components/arnmMaintenance/constants';

import { useAssignees, useServicePoints } from 'hooks/common';
import {
  handleArnmMaintenanceTaskData,
  handleArnmAssigneeData,
  handleVehicleData,
} from './utils';

const stages = orderBy(
  Object.keys(ARNM_MAINTENANCE_TASK_STATUSES_NAMES).reduce((acc, cur) => {
    acc.push({
      text: ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].label,
      value: Number(cur),
      order: ARNM_MAINTENANCE_TASK_STATUSES_NAMES[cur].order,
    });

    return acc;
  }, []),
  'order'
);

export const useArnmMaintenanceTaskOperations = () => {
  const router = useRouter();
  const { assignees, isFetching: isAssigneesFetching } = useAssignees();
  const { servicePoints, isFetching: isServicePointsFetching } =
    useServicePoints();
  const arnmMaintenanceTaskId = router.query?.arnmMaintenanceTaskId;

  const arnmMaintenanceTaskResponse = useQuery(
    [QUERY_KEYS.GET_ARNM_MAINTENANCE_TASK_KEY, arnmMaintenanceTaskId],
    () => boardsServices.getTaskById(arnmMaintenanceTaskId),
    {
      enabled: !!arnmMaintenanceTaskId,
    }
  );

  const carId = arnmMaintenanceTaskResponse?.data?.data?.customFields?.carId;

  const vehicleResponse = useQuery(
    [QUERY_KEYS.GET_VEHICLE_BY_INTERNAL_ID_KEY, carId],
    () => fleetVehiclesServices.getVehicleByInternalId(carId),
    {
      enabled: arnmMaintenanceTaskResponse.isFetched && !!carId,
    }
  );

  const vehicle = handleVehicleData(vehicleResponse?.data?.data);
  const arnmAssignees = handleArnmAssigneeData(assignees);

  const arnmMaintenanceTask = handleArnmMaintenanceTaskData(
    arnmMaintenanceTaskResponse?.data?.data,
    servicePoints,
    assignees,
    stages
  );

  const updateTask = async (payload = {}) => {
    try {
      if (payload?.cardId) {
        const id = payload?.cardId;
        delete payload?.cardId;
        await boardsServices.updateTask(id, payload);
      } else {
        await boardsServices.updateTask(payload?.id, payload);
      }
    } catch (error) {
      showError(error);
    }
  };

  const loading =
    arnmMaintenanceTaskResponse?.isFetching ||
    vehicleResponse?.isFetching ||
    isServicePointsFetching ||
    isAssigneesFetching;

  useEffect(() => {
    if (arnmMaintenanceTaskResponse?.isError) {
      showError(arnmMaintenanceTaskResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arnmMaintenanceTaskResponse?.isError]);

  useEffect(() => {
    if (vehicleResponse?.isError) {
      showError(vehicleResponse?.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleResponse?.isError]);

  return {
    loading,
    task: arnmMaintenanceTask,
    taskId: arnmMaintenanceTaskId,
    stages,
    assignees,
    arnmAssignees,
    servicePoints,
    vehicle,
    getArnmMaintenanceTask: arnmMaintenanceTaskResponse.refetch,
    updateTask,
  };
};
